import { CartLinesUpdateEvent } from '@shopify/events';

class WonderWallCollectionLanding extends HTMLElement {
  connectedCallback() {
    this.hideLegacyCollectionSurfaces();
    this.grid = this.querySelector('[data-ww-grid]');
    this.pagination = this.querySelector('[data-ww-pagination]');
    this.pageSize = Number(this.dataset.pageSize) || 60;
    this.productEvents();
    this.tabs();
    this.description();
    this.filters();
    this.searchAndSort();
    this.paginationEvents();
    this.moveSoldOutProductsToEnd();

    const params = new URLSearchParams(location.search);
    const query = params.get('ww_q')?.trim() || '';
    const price = params.get('ww_price') || '';
    const searchInput = this.querySelector('[name="ww_q"]');
    if (searchInput) searchInput.value = query;
    const priceInput = this.querySelector(`[data-ww-price-filter][value="${CSS.escape(price)}"]`);
    if (priceInput) priceInput.checked = true;
    if (query || price) this.applyClientQuery(query, price);
  }

  hideLegacyCollectionSurfaces() {
    document.body.classList.add('ww-has-collection-landing');

    const legacySelectors = [
      '#fdt-grid-container',
      'findter-search-filters',
      'fdt-search-filters',
      '.findter-search-filters',
      '.search-filters',
      'collection-component',
      'results-list.product-grid-container',
      '.section.product-grid-container',
    ];

    const removeLegacy = () => {
      document.querySelectorAll(legacySelectors.join(',')).forEach((element) => {
        if (!element.closest('ww-collection-landing')) element.remove();
      });
    };

    removeLegacy();

    this.legacyObserver?.disconnect();
    this.legacyObserver = new MutationObserver(removeLegacy);
    this.legacyObserver.observe(document.body, { childList: true, subtree: true });
  }

  tabs() {
    this.querySelectorAll('[data-ww-tab]').forEach((tab) => {
      tab.addEventListener('click', () => {
        this.querySelectorAll('[data-ww-tab]').forEach((item) => item.setAttribute('aria-selected', String(item === tab)));
        this.querySelectorAll('[data-ww-panel]').forEach((panel) => panel.classList.toggle('is-hidden', panel.dataset.wwPanel !== tab.dataset.wwTab));
      });
    });
  }

  description() {
    const toggle = this.querySelector('[data-ww-description-toggle]');
    const description = this.querySelector('[data-ww-description]');
    if (!toggle || !description) return;
    toggle.addEventListener('click', () => {
      const expanded = !description.classList.contains('is-expanded');
      description.classList.toggle('is-expanded', expanded);
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.textContent = expanded ? 'Read less' : 'Read more';
      if (matchMedia('(max-width: 749px)').matches) this.toggleBackdrop(expanded);
    });
  }

  filters() {
    const sheet = this.querySelector('[data-ww-filter-sheet]');
    this.querySelector('[data-ww-filter-open]')?.addEventListener('click', () => {
      sheet?.classList.add('is-open');
      this.toggleBackdrop(true);
    });
    this.querySelector('[data-ww-filter-close]')?.addEventListener('click', () => this.closeSheet());
    this.querySelector('[data-ww-backdrop]')?.addEventListener('click', () => {
      this.closeSheet();
      const description = this.querySelector('[data-ww-description]');
      description?.classList.remove('is-expanded');
      this.querySelector('[data-ww-description-toggle]')?.setAttribute('aria-expanded', 'false');
    });
    this.querySelectorAll('[data-ww-filter-more]').forEach((button) => {
      button.addEventListener('click', () => {
        const options = button.closest('.ww-filter-group__options');
        const expanded = options?.classList.toggle('is-expanded');
        button.textContent = expanded ? 'Show less' : 'Show more';
      });
    });
    this.querySelectorAll('[data-ww-select-all]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const inputs = [...this.querySelectorAll(`[data-ww-tag-filter="${CSS.escape(button.dataset.wwSelectAll)}"]`)];
        const allChecked = inputs.length && inputs.every((input) => input.checked);
        inputs.forEach((input) => { input.checked = !allChecked; });
      });
    });
    this.querySelector('[data-ww-clear-filters]')?.addEventListener('click', () => {
      this.querySelectorAll('[data-ww-tag-filter], [data-ww-price-filter]').forEach((input) => { input.checked = false; });
    });
    this.querySelector('[data-ww-apply-filters]')?.addEventListener('click', () => this.applyFilters());
    this.querySelectorAll('[data-ww-remove-tag]').forEach((button) => {
      button.addEventListener('click', () => {
        const active = this.activeTagHandles().filter((tag) => tag !== button.dataset.wwRemoveTag);
        this.navigateToTags(active);
      });
    });
  }

  closeSheet() {
    this.querySelector('[data-ww-filter-sheet]')?.classList.remove('is-open');
    this.toggleBackdrop(false);
  }

  toggleBackdrop(show) {
    const backdrop = this.querySelector('[data-ww-backdrop]');
    if (backdrop) backdrop.hidden = !show;
  }

  activeTagHandles() {
    return [...this.querySelectorAll('[data-ww-tag-filter]:checked')].map((input) => input.value);
  }

  applyFilters() {
    const price = this.querySelector('[data-ww-price-filter]:checked')?.value || '';
    const url = new URL(location.href);
    if (price) url.searchParams.set('ww_price', price); else url.searchParams.delete('ww_price');
    url.searchParams.delete('page');
    this.navigateToTags(this.activeTagHandles(), url.searchParams);
  }

  navigateToTags(tags, params = new URLSearchParams(location.search)) {
    const path = `/collections/${this.dataset.collectionHandle}${tags.length ? `/${tags.join('+')}` : ''}`;
    params.delete('page');
    location.assign(`${path}${params.toString() ? `?${params}` : ''}`);
  }

  searchAndSort() {
    this.querySelector('[data-ww-sort]')?.addEventListener('change', (event) => {
      const url = new URL(location.href);
      url.searchParams.set('sort_by', event.target.value);
      url.searchParams.delete('page');
      location.assign(url);
    });
    this.querySelector('[data-ww-search-form]')?.addEventListener('submit', (event) => {
      event.preventDefault();
      const query = new FormData(event.currentTarget).get('ww_q')?.toString().trim() || '';
      const url = new URL(location.href);
      if (query) url.searchParams.set('ww_q', query); else url.searchParams.delete('ww_q');
      url.searchParams.delete('page');
      history.replaceState({}, '', url);
      this.applyClientQuery(query, url.searchParams.get('ww_price') || '');
    });
  }

  paginationEvents() {
    this.addEventListener('click', async (event) => {
      const showMore = event.target.closest('[data-ww-show-more]');
      if (showMore) {
        if (this.clientMatches) {
          this.renderClientPage(this.clientPage + 1, true);
        } else {
          await this.loadNextPage(showMore);
        }
        return;
      }
      const clientPage = event.target.closest('[data-ww-client-page]');
      if (clientPage) this.renderClientPage(Number(clientPage.dataset.wwClientPage), false);
    });
  }

  async loadNextPage(button) {
    if (button.disabled) return;
    button.disabled = true;
    button.textContent = 'Loading…';
    try {
      const url = new URL(button.dataset.nextUrl, location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const html = await fetch(url).then((response) => {
        if (!response.ok) throw new Error('Unable to load more products.');
        return response.text();
      });
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      documentFragment.querySelectorAll('[data-ww-grid] > *').forEach((item) => this.grid.append(item));
      this.moveSoldOutProductsToEnd();
      const nextPagination = documentFragment.querySelector('[data-ww-pagination]');
      if (nextPagination && this.pagination) this.pagination.replaceWith(nextPagination);
      else this.pagination?.remove();
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Try again';
    }
  }

  async applyClientQuery(query, priceRange) {
    if (!this.grid) return;
    const count = this.querySelector('[data-ww-result-count]');
    if (count) count.textContent = 'Loading matching products…';
    this.grid.setAttribute('aria-busy', 'true');
    try {
      const totalPages = Number(this.grid.dataset.totalPages) || 1;
      const base = new URL(location.href);
      base.searchParams.delete('page');
      base.searchParams.delete('section_id');
      base.searchParams.delete('ww_q');
      base.searchParams.delete('ww_price');
      const requests = [];
      for (let page = 1; page <= totalPages; page += 1) {
        const url = new URL(base);
        url.searchParams.set('page', String(page));
        url.searchParams.set('section_id', this.dataset.sectionId);
        requests.push(fetch(url).then((response) => response.text()));
      }
      const pages = await Promise.all(requests);
      const found = new Map();
      pages.forEach((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        parsed.querySelectorAll('[data-ww-product-card]').forEach((card) => found.set(card.dataset.productId, card.outerHTML));
      });
      const normalized = query.toLowerCase();
      const [minimum, maximum] = priceRange ? priceRange.split('-').map(Number) : [0, 0];
      this.clientMatches = [...found.values()].filter((markup) => {
        const holder = document.createElement('div');
        holder.innerHTML = markup;
        const card = holder.firstElementChild;
        const matchesQuery = !normalized || card.dataset.search.includes(normalized);
        const price = Number(card.dataset.price);
        const matchesPrice = !priceRange || (price >= minimum && (maximum === 0 || price < maximum));
        return matchesQuery && matchesPrice;
      }).sort((first, second) => Number(this.markupIsAvailable(second)) - Number(this.markupIsAvailable(first)));
      this.renderClientPage(1, false);
    } catch (error) {
      if (count) count.textContent = 'Unable to search these results. Please try again.';
    } finally {
      this.grid.removeAttribute('aria-busy');
    }
  }

  markupIsAvailable(markup) {
    const holder = document.createElement('div');
    holder.innerHTML = markup;
    return holder.firstElementChild?.dataset.available === 'true';
  }

  moveSoldOutProductsToEnd() {
    if (!this.grid) return;
    const cards = [...this.grid.querySelectorAll(':scope > [data-ww-product-card]')];
    if (cards.length < 2) return;
    const orderedCards = cards
      .map((card, index) => ({ card, index, available: card.dataset.available === 'true' }))
      .sort((first, second) => Number(second.available) - Number(first.available) || first.index - second.index)
      .map(({ card }) => card);
    const slots = cards.map(() => document.createComment('WonderWall product slot'));
    cards.forEach((card, index) => card.replaceWith(slots[index]));
    slots.forEach((slot, index) => slot.replaceWith(orderedCards[index]));
  }

  renderClientPage(page, append) {
    if (!this.clientMatches) return;
    const totalPages = Math.max(1, Math.ceil(this.clientMatches.length / this.pageSize));
    this.clientPage = Math.min(Math.max(page, 1), totalPages);
    const start = (this.clientPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    const markup = this.clientMatches.slice(start, end).join('');
    if (append) this.grid.insertAdjacentHTML('beforeend', markup); else this.grid.innerHTML = markup || '<p class="ww-results__empty">No products match your search.</p>';
    this.moveSoldOutProductsToEnd();
    const count = this.querySelector('[data-ww-result-count]');
    if (count) count.textContent = `${this.clientMatches.length} results`;
    const pages = Array.from({ length: totalPages }, (_, index) => {
      const value = index + 1;
      return value === this.clientPage ? `<span aria-current="page">${value}</span>` : `<button type="button" data-ww-client-page="${value}">${value}</button>`;
    }).join('');
    const pagination = `<nav class="ww-pagination" aria-label="Filtered result pages" data-ww-pagination>${this.clientPage < totalPages ? '<button type="button" class="ww-button" data-ww-show-more>Show more products</button>' : ''}<div class="ww-pagination__pages">${pages}</div></nav>`;
    if (this.pagination?.isConnected) this.pagination.outerHTML = pagination;
    else this.querySelector('.ww-results')?.insertAdjacentHTML('beforeend', pagination);
    this.pagination = this.querySelector('[data-ww-pagination]');
    if (!append) this.querySelector('.ww-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  productEvents() {
    this.addEventListener('click', (event) => {
      const more = event.target.closest('[data-ww-variants-more]');
      if (more) {
        const wrapper = more.closest('.ww-card__variants');
        const expanded = wrapper?.classList.toggle('is-expanded');
        more.setAttribute('aria-expanded', String(expanded));
        more.textContent = expanded ? '−' : '+';
        return;
      }
      const variant = event.target.closest('[data-ww-variant]');
      if (variant) this.selectVariant(variant);
      const add = event.target.closest('[data-ww-add]');
      if (add) this.changeQuantity(add.closest('[data-ww-purchase]'), 1, true);
      const plus = event.target.closest('[data-ww-plus]');
      if (plus) this.changeQuantity(plus.closest('[data-ww-purchase]'), 1, false);
      const minus = event.target.closest('[data-ww-minus]');
      if (minus) this.changeQuantity(minus.closest('[data-ww-purchase]'), -1, false);
    });
  }

  selectVariant(button) {
    const card = button.closest('[data-ww-product-card]');
    card.querySelectorAll('[data-ww-variant]').forEach((item) => item.classList.toggle('is-selected', item === button));
    card.querySelector('[data-ww-purchase]').dataset.variantId = button.dataset.variantId;
    card.querySelector('[data-ww-price]').textContent = button.dataset.price;
    const compare = card.querySelector('[data-ww-compare]');
    compare.textContent = button.dataset.comparePrice || '';
    compare.classList.toggle('is-hidden', !button.dataset.comparePrice);
    if (button.dataset.image) card.querySelector('.ww-card__image').src = button.dataset.image;
    const add = card.querySelector('[data-ww-add]');
    add.disabled = button.dataset.available !== 'true';
    add.textContent = add.disabled ? 'Sold out' : 'Add to basket';
    card.querySelector('[data-ww-stepper]').hidden = true;
    add.hidden = false;
    card.querySelector('[data-ww-purchase]').dataset.quantity = '0';
  }

  async changeQuantity(purchase, delta, isAdd) {
    if (!purchase || purchase.dataset.busy === 'true') return;
    const variantId = purchase.dataset.variantId;
    const current = Number(purchase.dataset.quantity) || 0;
    const target = Math.max(0, current + delta);
    purchase.dataset.busy = 'true';
    const status = purchase.querySelector('[data-ww-status]');
    status.textContent = '';
    const deferred = CartLinesUpdateEvent.createPromise();
    purchase.dispatchEvent(new CartLinesUpdateEvent({
      action: isAdd ? 'add' : 'update',
      context: 'collection',
      lines: [{ merchandiseId: variantId, quantity: target }],
      promise: deferred.promise,
    }));
    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId).filter(Boolean);
      let response;
      if (isAdd || current === 0) {
        response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds, sections_url: location.pathname }),
        }).then((result) => result.json());
      } else {
        const cartBefore = await fetch(`${window.Shopify.routes.root}cart.js`).then((result) => result.json());
        const line = cartBefore.items.find((item) => String(item.variant_id) === String(variantId));
        response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: line?.key || variantId, quantity: target, sections: sectionIds, sections_url: location.pathname }),
        }).then((result) => result.json());
      }
      if (response.status) throw new Error(response.description || response.message || 'Unable to update cart.');
      const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then((result) => result.json());
      purchase.dataset.quantity = String(target);
      purchase.querySelector('[data-ww-quantity]').textContent = String(target);
      purchase.querySelector('[data-ww-add]').hidden = target > 0;
      purchase.querySelector('[data-ww-stepper]').hidden = target === 0;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, itemCount: target, sections: response.sections, source: 'wonderwall-collection' },
      });
    } catch (error) {
      status.textContent = error.message;
      deferred.reject(error);
    } finally {
      purchase.dataset.busy = 'false';
    }
  }
}

if (!customElements.get('ww-collection-landing')) customElements.define('ww-collection-landing', WonderWallCollectionLanding);
